/** Scoped device picker for the manual-create dialog (spec §8): dispatch picks
 *  the affected meter/transformer/feeder/substation by NAME/NUMBER instead of
 *  typing a raw internal id. Reuses the platform's existing GET /search
 *  endpoint + debounced-dropdown idiom (see components/search/SearchBar.tsx);
 *  results are filtered client-side to the chosen scope. The resolved numeric
 *  id is what the form submits; the chosen device's label stays visible for
 *  confirmation. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { get } from '../../api/client.ts'
import type { OutageScope } from '../../types/oms.ts'

/** Shape of one GET /search result (api/v1/search.py). */
interface SearchResult {
  type: string
  id: number
  label: string
  sublabel: string | null
  // Point for meters/transformers/substations; LineString for feeder routes
  location: { type: string; coordinates: number[] | number[][] } | null
}

/** The resolved selection handed back to the form. */
export interface SelectedDevice {
  id: number
  label: string
  sublabel: string | null
}

export function DevicePicker({
  scope,
  value,
  onChange,
}: {
  scope: OutageScope
  value: SelectedDevice | null
  onChange: (device: SelectedDevice | null) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [searched, setSearched] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const doSearch = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setResults([])
        setIsOpen(false)
        setSearched(false)
        return
      }
      try {
        const data = await get<SearchResult[]>(
          `/search?q=${encodeURIComponent(q)}&limit=20`,
        )
        const scoped = data.filter(r => r.type === scope)
        setResults(scoped)
        setSearched(true)
        setIsOpen(true)
      } catch {
        setResults([])
        setIsOpen(false)
        setSearched(false)
      }
    },
    [scope],
  )

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => doSearch(query), 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, doSearch])

  // Chosen device — show it for confirmation with a Change affordance.
  if (value) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 border border-emerald-300 bg-emerald-50 rounded-md px-2 py-1.5">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-800 truncate">
            {value.label}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {value.sublabel ? `${value.sublabel} · ` : ''}
            {scope} id {value.id}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(null)
            setQuery('')
            setResults([])
            setIsOpen(false)
            setSearched(false)
          }}
          className="text-xs text-blue-600 hover:underline shrink-0"
        >
          Change
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder={`Search ${scope}s by name or number…`}
        aria-label={`Search ${scope}`}
        className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
      />
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto z-50">
          {results.map(r => (
            <button
              key={`${r.type}-${r.id}`}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
              onMouseDown={() => {
                onChange({ id: r.id, label: r.label, sublabel: r.sublabel })
                setIsOpen(false)
                setQuery('')
              }}
            >
              <div className="truncate font-medium text-gray-800">{r.label}</div>
              {r.sublabel && (
                <div className="truncate text-xs text-gray-500">{r.sublabel}</div>
              )}
            </button>
          ))}
          {searched && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">
              No {scope} matches "{query}".
            </div>
          )}
        </div>
      )}
    </div>
  )
}
